import { onEmailImagesReady } from './emailImages';
import { sanitizeHtml } from './sanitizeHtml';

/**
 * Stored mail bodies, turned into what the contact overlay puts on screen.
 *
 * Rendering one body parses its HTML four times over — once to drop the quoted reply chain,
 * twice inside `sanitizeHtml`, and once more when the browser takes the sanitized result — and
 * the bodies are not small: a well-worked venue carries ~150 messages whose median body is
 * ~117 KB, around 17 MB in all. Doing that per card on every render is what made the overlay
 * crawl, because anything that replaces `contacts` (the Gmail sync on open, a saved call note,
 * a mark-as-read) hands the list fresh objects, breaks the cards' `memo` and replays the pass.
 *
 * Two things keep it off the render path:
 *
 *  - a body is parsed once and the result kept, keyed by the Gmail message it belongs to.
 *    A stored body never changes, so the entry cannot go stale;
 *  - a collapsed card shows two clamped lines, so it is given a plain-text preview read off the
 *    head of the body. Only the card the user expands is parsed in full.
 */

/** Whether a body is HTML rather than the plain text some mail — and every typed note — carries. */
export function isHtml(text: string): boolean {
  // Deliberately `[^>]*` rather than `[\s\S]*`: the greedy form scans to the end of a 200 KB
  // body and backtracks to the last `>`, and this is called on every body.
  return /<[a-z][^>]*>/i.test(text);
}

/** Strip Gmail quoted reply chains from an email body, keeping only the new content. */
export function stripQuotedReplies(text: string): string {
  if (isHtml(text)) {
    // For HTML content, remove Gmail quote blocks and Outlook-style quoted content
    const parser = new DOMParser();
    const doc = parser.parseFromString(text, 'text/html');
    doc.querySelectorAll('.gmail_quote, .gmail_extra, .gmail_attr, blockquote[type="cite"], div[id="appendonsend"]').forEach(el => el.remove());
    return doc.body.innerHTML;
  }

  let result = text;

  // Cut everything from "On ... wrote:" onwards ([\s\S] to match across newlines)
  const quoteMatch = result.match(/On [\s\S]{10,200} wrote:/);
  if (quoteMatch?.index !== undefined) {
    result = result.substring(0, quoteMatch.index);
  }

  // Cut from "---------- Forwarded message" onwards
  const fwdIdx = result.indexOf('---------- Forwarded message');
  if (fwdIdx >= 0) {
    result = result.substring(0, fwdIdx);
  }

  // Remove lines starting with ">" (quoted text)
  result = result
    .split('\n')
    .filter(line => !line.trimStart().startsWith('>'))
    .join('\n');

  return result.trim();
}

export interface MailBodySource {
  /** The stored body. */
  body: string;
  /** Used when the body is empty, or turns out to be nothing but a quoted chain — for a thread
   *  message that is Gmail's own snippet. */
  fallback?: string;
  /** The Gmail message the body belongs to, when it has one. Doubles as the cache key. */
  messageId?: string;
}

export interface RenderedBody {
  /** Sanitized HTML for `dangerouslySetInnerHTML`, or null when the body is plain text. */
  html: string | null;
  /** The body as text, when it is not HTML. */
  text: string;
}

/**
 * Entries are evicted least-recently-used first, under both a count and a size ceiling: one
 * venue's thread can hold a few hundred messages, and a single rendered body still runs to
 * tens of KB once the quoted chain is gone.
 */
const rendered = new Map<string, RenderedBody>();
const MAX_CACHED_BODIES = 400;
const MAX_CACHED_CHARS = 4_000_000;
let cachedChars = 0;

// A body rendered before the image library finished loading cached the placeholder chip instead
// of the image. Nothing else would ever evict it — the key is a Gmail message id and a stored
// body never changes — so the arrival of the images is the one event that invalidates this cache.
onEmailImagesReady(() => {
  rendered.clear();
  cachedChars = 0;
});

const sizeOf = (body: RenderedBody) => (body.html ?? body.text).length;

function render({ body, fallback }: MailBodySource): RenderedBody {
  const content = stripQuotedReplies(body) || (fallback ? stripQuotedReplies(fallback) : '');
  return isHtml(content) ? { html: sanitizeHtml(content), text: '' } : { html: null, text: content };
}

/**
 * The full body, ready to render. A message without a Gmail id — a scheduled or failed send, a
 * note typed here — was composed in the app and is small enough to render on the spot, so it is
 * simply not cached.
 */
export function renderMailBody(src: MailBodySource): RenderedBody {
  const cacheKey = src.messageId;
  if (!cacheKey) return render(src);

  const hit = rendered.get(cacheKey);
  if (hit) {
    // Re-insert so the iteration order below stays least-recently-used first
    rendered.delete(cacheKey);
    rendered.set(cacheKey, hit);
    return hit;
  }

  const body = render(src);
  rendered.set(cacheKey, body);
  cachedChars += sizeOf(body);
  while (rendered.size > 1 && (rendered.size > MAX_CACHED_BODIES || cachedChars > MAX_CACHED_CHARS)) {
    const oldest = rendered.keys().next();
    if (oldest.done) break;
    cachedChars -= sizeOf(rendered.get(oldest.value)!);
    rendered.delete(oldest.value);
  }
  return body;
}

/** Visible characters a collapsed card needs: it clamps to two lines, so the preview only has
 *  to be long enough that a body with more to say still overflows them. */
const PREVIEW_CHARS = 600;
/** How much of an HTML body to read for that — past the `<head>` and the wrapper markup real
 *  mail opens with. Grown, rarely, when that turns out not to be enough. */
const PREVIEW_SLICE = 4096;

/** The opening tag of a quoted chain: the same blocks `stripQuotedReplies` removes. */
const QUOTE_START =
  /<(?:blockquote[^>]*\btype=["']?cite|[a-z][a-z0-9]*[^>]*\b(?:class=["'][^"']*\bgmail_(?:quote|extra|attr)|id=["']appendonsend))/i;
/** Elements whose text is markup, not content — what DOMPurify drops wholesale. */
const NON_CONTENT = /<(script|style|head|title)\b[\s\S]*?<\/\1\s*>/gi;
const NAMED_ENTITIES: Record<string, string> = {
  nbsp: ' ', amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", mdash: '—', ndash: '–', hellip: '…',
};

function decodeEntity(match: string, body: string): string {
  if (body[0] !== '#') return NAMED_ENTITIES[body.toLowerCase()] ?? match;
  const code = body[1] === 'x' || body[1] === 'X' ? parseInt(body.slice(2), 16) : Number(body.slice(1));
  return Number.isInteger(code) && code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : ' ';
}

function htmlToText(html: string): string {
  let out = html.replace(NON_CONTENT, ' ');
  // A `<style>` left open by the slice would otherwise spill its CSS into the preview
  const unclosed = out.search(/<(?:script|style)\b/i);
  if (unclosed >= 0) out = out.slice(0, unclosed);
  return out
    // The slice can land inside a tag; without this its attributes read as text
    .replace(/<[^>]*$/, ' ')
    // An image is content, so it gets a marker — a body that is nothing but a photo would
    // otherwise preview as blank, which reads as an empty mail
    .replace(/<img\b[^>]*>/gi, ' 🖼 ')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&(#x?[0-9a-f]+|[a-z]+);/gi, decodeEntity)
    .replace(/\s+/g, ' ')
    .trim();
}

function previewOf(raw: string): string {
  if (!raw) return '';
  if (!isHtml(raw)) return stripQuotedReplies(raw).replace(/\s+/g, ' ').trim().slice(0, PREVIEW_CHARS);

  for (let end = PREVIEW_SLICE; ; end *= 4) {
    const slice = raw.slice(0, end);
    // Everything before the quoted chain is all the new content there is, however short
    const quoted = slice.search(QUOTE_START);
    const text = htmlToText(quoted >= 0 ? slice.slice(0, quoted) : slice);
    if (quoted >= 0 || end >= raw.length || text.length >= PREVIEW_CHARS) {
      return text.slice(0, PREVIEW_CHARS);
    }
  }
}

/** A plain-text preview of a body, for a card that is showing only its first two lines. */
export function mailBodyPreview({ body, fallback }: MailBodySource): string {
  return previewOf(body) || (fallback ? previewOf(fallback) : '');
}
